import { forwardRef, JSX, memo, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import logger from '@gral/log';
import {
  DataGrid,
  IDataGridModels,
  IDataGridProps,
  TInheritedProps,
  TRows,
  IImperativeHandle,
  IDataGridConfig,
  IPostDataGridConfig,
  IAction,
  TTextType,
  ICustomDeleteModal,
} from '@ts/storybook';
//import DataGrid from './NewDataGrid';
import { newCClassEntity, ICompSetting, IConfigComponent, PaginatedResponseV3 } from '@ts/ui-models';
import { GridColDef, GridFeatureMode } from '@mui/x-data-grid-pro';
import { useRouterContext } from '@utils/context/RouterContext';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { getConfiguration } from '@modules/utils/AdvancedFetch';
import { fetchData, putData } from '@redux/slices/functionals/request/requestActions';
import { useNavigate } from 'react-router';
import { deleteEntity, patchEntity, postDataGridConfig, putEntity } from '@services/ApiService';
import ItemsColumn from '@pages/DataBase/Flavors/Items/ItemColumns';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { isEqual } from 'lodash';

export interface IWrapperDataGridProps<Multiple extends boolean> {
  configComponent: IConfigComponent;
  dataGridConfig?: IDataGridConfig;

  columns: GridColDef[];
  rows?: TRows<any>;
  dataGridMode?: GridFeatureMode;

  //DataGrid
  enabledFilters?: boolean;
  enabledSort?: boolean;
  rowHeight?: number;
  isMobile?: boolean;
  isEmbededMobile?: boolean;
  collapsableComponent?: any;
  propsDataGrid?: IDataGridProps<Multiple>['propsDataGrid'];

  //DataGridToolbar
  title?: TTextType;
  infoDataGrid?: TTextType;
  total?: number | null;
  withoutCreateEntity?: boolean;
  postDataGridConfig?: (level: string, componentId: string, dataGridConfig: IDataGridConfig, roleId?: string) => void;

  //QuickAccess
  withoutDefaultActions?: boolean;
  withoutActions?: boolean;
  extraActions?: IAction[];
  customDeleteModal?: ICustomDeleteModal;

  inheritedProps?: TInheritedProps<Multiple>;
  defaultCreationProperties?: object; //Flux or FromCRUD
}

type THandle = IImperativeHandle;
type TProps<Multiple extends boolean> = IWrapperDataGridProps<Multiple>;

const log = logger('WrapperDataGrid');
const WrapperDataGridInner = <Multiple extends boolean>(props: TProps<Multiple>, ref: React.Ref<THandle>) => {
  //log.info('Render', props);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    dataGridConfig: parentDataGridConfig,
    configComponent,
    enabledFilters,
    enabledSort,
    dataGridMode,
    rowHeight,
    collapsableComponent,
    title,
    infoDataGrid,
    total,
    withoutCreateEntity,
    extraActions,
    customDeleteModal,
    withoutDefaultActions,
    withoutActions,
    propsDataGrid,
    ...rest
  } = props;
  const { transferPropsRoute } = useRouterContext();
  const { isModal, isEntitySelector, toolbarProps } = props.inheritedProps ?? {};
  const filterID = transferPropsRoute.query?.filterID;
  const globalSettings = useAppSelector(state => state.global.settings);
  const listACL = useAppSelector(state => state.global.aclList);
  const isAdmin = useAppSelector(state => state.global.sessionBadge?.isAdmin);
  if (!globalSettings) return;
  const componentId = configComponent.componentId;
  const componentFilterId = isModal
    ? 'modal_' + componentId
    : toolbarProps?.inCollapsable
    ? 'collapsable_' + componentId
    : isEntitySelector
    ? 'entitySelector_' + componentId
    : filterID ?? componentId; // If is a quickModal or collapsable or entitySelector we don't use the URL filterID

  //#region Configuration & Settings
  const savedDataGridConfig = globalSettings.dataGridConfig?.[componentFilterId];
  const mergedDataGridConfig = { ...parentDataGridConfig, ...savedDataGridConfig };

  const mergedConfigComponent = {
    ...configComponent,
    componentId: componentFilterId,
    ACL: listACL[configComponent.aclId],
  };

  const componentSettings = globalSettings.componentSettings[componentId];
  const componentSettingsDefault = configComponent.defaultId ? globalSettings.componentSettings[configComponent.defaultId] : {};
  const mergedComponentSettings: ICompSetting = {
    ...componentSettingsDefault,
    ...componentSettings,
  };
  //#endregion

  //#region Fetch data
  const [isLoading, setIsLoading] = useState(1);

  const [rows, setRows] = useState<TRows<any>>(props.rows ?? { count: null, rows: [] });
  const [dataGridModels, setDataGridModels] = useState<IDataGridModels | null>(null);

  const getFetchData = useCallback(
    async (withoutPagination = false, withoutFilters = false, forDownload = false) => {
      if (!dataGridModels) return;
      const dataBodyTableV4 = getConfiguration(
        configComponent,
        { componentSettings: mergedComponentSettings, dataGridConfig: mergedDataGridConfig },
        dataGridModels,
        {
          withoutPagination,
          withoutFilters,
          forDownload,
        }
      );

      return (
        await dispatch(
          fetchData({ key: 'dataGrid', url: mergedConfigComponent.fetchURL ?? 'missing', dataBody: dataBodyTableV4 })
        )
      ).payload.data;
    },
    [dispatch, mergedConfigComponent, dataGridModels]
  );
  const setRowsFromFetchData = (data?: TRows<any> | PaginatedResponseV3<any[]> | any[]) => {
    const formatDataToClass = (rows: any[]) => {
      return rows.map(row => {
        const entity = { ...row.entity };
        const copyRow: any = newCClassEntity(row, props.configComponent.mainClass);
        if (entity) {
          // typed as any to add cEntity field. There is a collision with entity field that was removed before
          copyRow.cEntity = entity;
        }
        if (!copyRow.id) {
          copyRow.id = row.logicalId;
        }

        return copyRow;
      });
    };

    if (data) {
      if (data && typeof data === 'object' && 'count' in data) {
        //V1 or V2
        setRows({ count: data.count, rows: formatDataToClass(data.rows) });
      } else if (Array.isArray(data) && dataGridModels) {
        const count = dataGridModels.paginationModel.pageSize === data.length ? -1 : data.length; //ForReadings
        //V1 or V2
        setRows({ count: count, rows: formatDataToClass(data) });
        if (!data.length || data.length < dataGridModels.paginationModel.pageSize) {
          //setState({ ...state, dataGridMode: props.dataGridMode ?? 'client' });
        }
      } else if (!Array.isArray(data)) {
        //V3
        setRows({ count: data.rows ?? data.data.length, rows: formatDataToClass(data.data) });
      }
    }
    setIsLoading(0);
  };
  //#endregion

  //#region  Data
  const onPutEntity = (entityId, entity: any) => {
    return putEntity(dispatch, mergedConfigComponent, entityId, entity);
  };

  const onPatchEntity = (entityId, entity: any) => {
    return patchEntity(dispatch, mergedConfigComponent, entityId, entity);
  };

  const onDeleteEntity = entityId => {
    return deleteEntity(dispatch, mergedConfigComponent, entityId);
  };

  const onPostDataGridConfig = (data: IPostDataGridConfig) => {
    const { level, componentId, dataGridConfig, roleId } = data;
    //console.log('level,componentID,dataGRidConfig,roleID', level, componentId, dataGridConfig, roleId);
    return postDataGridConfig(dispatch, level, componentId, dataGridConfig, roleId);
  };
  //#endregion

  //#region Functions
  const notification = (text: TTextType) => {
    dispatch(globalActions.notification(text as string));
  };
  //#endregion

  const [isRefreshData, setIsRefreshData] = useState(false);
  const onRefresh = () => {
    log.info('onRefresh', isRefreshData);
    setIsRefreshData(prev => !prev);
  };
  const imperativeHandle: IImperativeHandle = {
    refreshDataGrid() {
      log.info('Refresh data grid from parent or children');
      onRefresh();
    },
    getFetchData,
  };

  useImperativeHandle(ref, () => imperativeHandle);

  const useEffectFetchData = (deps: React.DependencyList) => {
    useEffect(() => {
      if (dataGridModels) {
        setIsLoading(1);
        if (!props.rows) {
          log.info('0 - Fetching data');
          getFetchData()
            .then(data => {
              if (data) {
                setRowsFromFetchData(data);
              } else {
                setIsLoading(0);
              }
            })
            .catch(err => {
              //log.error('Fetch error', err);
              setIsLoading(0);
            });
        } else {
          log.info('Set rows from props');
          setRowsFromFetchData(props.rows);
          setIsLoading(0);
        }
      }
    }, deps);
  };

  useEffectFetchData([dataGridModels, isRefreshData]);
  log.info('rows', rows);

  return (
    <DataGrid
      {...rest}
      configComponent={mergedConfigComponent}
      dataGridConfig={mergedDataGridConfig}
      componentSettings={mergedComponentSettings}
      notification={notification}
      navigate={navigate}
      isLoading={!!isLoading}
      rows={rows}
      onChangeDataGrid={v => {
        if (!isEqual(v, dataGridModels)) {
          setDataGridModels(v);
        }
      }}
      onPutEntity={onPutEntity}
      onPatchEntity={onPatchEntity}
      onDeleteEntity={onDeleteEntity}
      imperativeHandle={imperativeHandle}
      propsDataGrid={{
        rowHeight,
        dataGridMode,
        enabledFilters,
        enabledSort,
        collapsableComponent,
        ...(propsDataGrid ?? {}),
      }}
      propsDataGridToolbar={{
        title,
        infoDataGrid,
        total,
        withoutCreateEntity,
        isAdmin,
        onPostDataGridConfig,
        ItemsColumn,
      }}
      propsDataGridQuickAccess={{
        withoutDefaultActions,
        withoutActions,
        extraActions,
        customDeleteModal,
      }}
    />
  );
};

export const WrapperDataGrid = memo(forwardRef(WrapperDataGridInner)) as <Multiple extends boolean>(
  props: TProps<Multiple> & { ref?: React.Ref<THandle> }
) => JSX.Element;

export default WrapperDataGrid;
